import { useState, useRef } from 'react';
import { ArrowLeft, Plus, Trash2, AlertTriangle, Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const PROPERTY_TYPES = [
  'Residential Under Construction',
  'Residential Ready to Move',
  'Commercial Under Construction',
  'Commercial Ready to Move',
  'Land / Plot',
  'Mixed Use'
];

const DEFAULT_ADDRESSES = [
  { id: 'default', label: 'Per Annum HQ — 123 Business Park, Sector 44, Gurgaon, Haryana 122003' },
  { id: 'branch', label: 'Per Annum Branch — 456 Tower, Connaught Place, New Delhi 110001' },
];

export default function AdminUnitForm({ projectId, onBack }) {
  const [formData, setFormData] = useState({
    // Unit identifiers
    unitName: '',
    unitNumber: '',
    tower: '',
    floor: '',
    configuration: '',
    propertyType: 'Residential Under Construction',
    // Area
    superArea: '',
    carpetArea: '',
    // LLP
    llpName: '',
    llpin: '',
    llpRegisteredOffice: DEFAULT_ADDRESSES[0].label,
    llpRegisteredOfficeType: 'default',
    llpEffectiveDate: '',
    llpBankName: '',
    llpBankAccountName: '',
    llpIfsc: '',
    llpAccountNumber: '',
    // Designated Partners
    dp1Name: '',
    dp1Din: '',
    dp2Name: '',
    dp2Din: '',
    // Financial
    unitValue: '',
    capitalTarget: '',
    minInvestment: 500000,
    maxInvestment: 10000000,
    // Agreement terms
    holdingPeriod: '6 Months',
    targetAppreciation: '5% annualised increase in Book Value',
    // Documents
    bbaDate: '',
  });

  // Document uploads
  const [documents, setDocuments] = useState({ allotmentLetter: null, bba: null });
  const allotmentRef = useRef(null);
  const bbaRef = useRef(null);

  // Payment plan (Zoho Annexure-D) — the master builder milestone schedule
  // Capital calls to investors will be derived from this with a buffer period (future feature)
  const [paymentPlan, setPaymentPlan] = useState([
    { slotNumber: 1, percentage: 25, milestone: 'On Booking', description: '', expectedDate: '' },
  ]);

  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('identifiers');

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Payment plan handlers
  const addPaymentSlot = () => {
    if (paymentPlan.length >= 5) { toast.error('Maximum 5 payment milestones'); return; }
    const n = paymentPlan.length + 1;
    setPaymentPlan([...paymentPlan, { slotNumber: n, percentage: 0, milestone: '', description: '', expectedDate: '' }]);
  };
  const removePaymentSlot = (idx) => {
    if (paymentPlan.length <= 1) { toast.error('At least one milestone required'); return; }
    setPaymentPlan(paymentPlan.filter((_, i) => i !== idx).map((s, i) => ({ ...s, slotNumber: i + 1 })));
  };
  const updatePaymentSlot = (idx, field, value) => {
    setPaymentPlan(paymentPlan.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const ppTotal = paymentPlan.reduce((s, p) => s + (parseInt(p.percentage) || 0), 0);
  const firstSlotNotBooking = paymentPlan.length > 0 && paymentPlan[0].milestone && !paymentPlan[0].milestone.toLowerCase().includes('booking');

  const applyPaymentPlanTemplate = () => {
    setPaymentPlan([
      { slotNumber: 1, percentage: 25, milestone: 'On Booking', description: 'Initial booking amount', expectedDate: '' },
      { slotNumber: 2, percentage: 25, milestone: 'On 1st Floor Slab Completion', description: '', expectedDate: '' },
      { slotNumber: 3, percentage: 25, milestone: 'On Top Floor Slab Completion', description: '', expectedDate: '' },
      { slotNumber: 4, percentage: 20, milestone: 'On Application for OC', description: '', expectedDate: '' },
      { slotNumber: 5, percentage: 5, milestone: 'On Possession', description: '', expectedDate: '' },
    ]);
    toast.success('Payment plan template applied');
  };

  const autoCalculateTarget = () => {
    if (formData.unitValue) update('capitalTarget', formData.unitValue * 0.30);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (ppTotal !== 100) { toast.error(`Payment plan percentages must total exactly 100% (currently ${ppTotal}%)`); return; }

    // Validate payment plan percentages are integers
    for (const slot of paymentPlan) {
      if (parseInt(slot.percentage) !== parseFloat(slot.percentage)) {
        toast.error(`Payment plan percentages must be whole integers (slot ${slot.slotNumber} has ${slot.percentage}%)`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        unitName: formData.unitName,
        unitNumber: formData.unitNumber || null,
        tower: formData.tower || null,
        floor: formData.floor || null,
        propertyType: formData.propertyType || null,
        superArea: parseFloat(formData.superArea) || null,
        carpetArea: parseFloat(formData.carpetArea) || null,
        llpName: formData.llpName,
        llpin: formData.llpin || null,
        llpRegisteredOffice: formData.llpRegisteredOffice || null,
        llpEffectiveDate: formData.llpEffectiveDate || null,
        llpBankAccountName: formData.llpBankAccountName || null,
        llpBankName: formData.llpBankName || null,
        llpIfsc: formData.llpIfsc || null,
        llpAccountNumber: formData.llpAccountNumber || null,
        designatedPartners: {
          dp1: { name: formData.dp1Name, din: formData.dp1Din },
          dp2: { name: formData.dp2Name, din: formData.dp2Din },
        },
        unitValue: parseFloat(formData.unitValue) || 0,
        capitalTarget: parseFloat(formData.capitalTarget) || parseFloat(formData.unitValue) * 0.3,
        minInvestment: parseFloat(formData.minInvestment) || 500000,
        maxInvestment: parseFloat(formData.maxInvestment) || 10000000,
        unitSize: formData.superArea ? `${formData.superArea} sqft` : '',
        configuration: formData.configuration,
        bbaDate: formData.bbaDate || null,
        agreementTerms: {
          holdingPeriod: formData.holdingPeriod,
          targetAppreciation: formData.targetAppreciation,
        },
        paymentPlan: paymentPlan.map((p, i) => ({
          slotNumber: i + 1,
          percentage: parseInt(p.percentage),
          milestone: p.milestone,
          description: p.description || '',
          expectedDate: p.expectedDate || null,
        })),
      };

      await api.post(`/admin/projects/${projectId}/units`, payload);
      toast.success('Unit created successfully');
      setTimeout(() => onBack(), 1000);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create unit';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 px-4 py-3 text-white text-sm focus:border-white/30 focus:outline-none transition-colors";
  const labelCls = "text-xs text-gray-500 uppercase tracking-wider mb-2 block";

  const sections = [
    { id: 'identifiers', label: 'Unit Identity' },
    { id: 'llp', label: 'LLP Details' },
    { id: 'designated-partners', label: 'Designated Partners' },
    { id: 'financial', label: 'Financial' },
    { id: 'documents', label: 'Documents' },
    { id: 'agreement', label: 'Agreement Terms' },
    { id: 'payment-plan', label: 'Payment Schedule' },
  ];

  const handleFileUpload = (type, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files are accepted'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    setDocuments(prev => ({ ...prev, [type]: file }));
    toast.success(`${type === 'allotmentLetter' ? 'Allotment Letter' : 'BBA'} uploaded`);
  };

  const removeFile = (type) => {
    setDocuments(prev => ({ ...prev, [type]: null }));
    if (type === 'allotmentLetter' && allotmentRef.current) allotmentRef.current.value = '';
    if (type === 'bba' && bbaRef.current) bbaRef.current.value = '';
  };

  const handleOfficeChange = (value) => {
    if (value === 'custom') {
      update('llpRegisteredOfficeType', 'custom');
      update('llpRegisteredOffice', '');
    } else {
      const addr = DEFAULT_ADDRESSES.find(a => a.id === value);
      update('llpRegisteredOfficeType', value);
      update('llpRegisteredOffice', addr ? addr.label : '');
    }
  };

  return (
    <div className="p-10" data-testid="unit-creation-form">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white mb-6 flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Back to Project
      </button>
      <h1 className="text-2xl font-light text-white mb-2">Add Unit</h1>
      <p className="text-sm text-gray-500 mb-8">All fields marked with * are required for Zoho document generation</p>

      {/* Section navigation */}
      <div className="flex gap-1 mb-8 border-b border-white/10 pb-0">
        {sections.map(s => (
          <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2.5 text-xs uppercase tracking-wider transition-all border-b-2 -mb-px ${activeSection === s.id ? 'border-[#4ADE80] text-white' : 'border-transparent text-gray-500 hover:text-gray-300'}`}
            data-testid={`section-tab-${s.id}`}>
            {s.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        {/* Section 1: Unit Identity */}
        {activeSection === 'identifiers' && (
          <div className="border border-white/10 p-8 mb-8" data-testid="section-identifiers">
            <h2 className="text-lg text-white mb-6">Unit Identity & Physical Details</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Unit Display Name *</label>
                <input required value={formData.unitName} onChange={e => update('unitName', e.target.value)} className={inputCls} placeholder="Unit 501 - Tower A" data-testid="input-unit-name" />
              </div>
              <div>
                <label className={labelCls}>Unit Number *</label>
                <input value={formData.unitNumber} onChange={e => update('unitNumber', e.target.value)} className={inputCls} placeholder="501" data-testid="input-unit-number" />
                <p className="text-xs text-gray-600 mt-1">Used in Zoho LLP agreement</p>
              </div>
              <div>
                <label className={labelCls}>Tower / Block *</label>
                <input value={formData.tower} onChange={e => update('tower', e.target.value)} className={inputCls} placeholder="Tower A" data-testid="input-tower" />
              </div>
              <div>
                <label className={labelCls}>Floor *</label>
                <input value={formData.floor} onChange={e => update('floor', e.target.value)} className={inputCls} placeholder="5th Floor" data-testid="input-floor" />
              </div>
              <div>
                <label className={labelCls}>Configuration *</label>
                <input required value={formData.configuration} onChange={e => update('configuration', e.target.value)} className={inputCls} placeholder="3 BHK" data-testid="input-configuration" />
              </div>
              <div>
                <label className={labelCls}>Property Type *</label>
                <select value={formData.propertyType} onChange={e => update('propertyType', e.target.value)} className={inputCls} data-testid="select-property-type">
                  {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Super Built-Up Area (sq ft) *</label>
                <input type="number" value={formData.superArea} onChange={e => update('superArea', e.target.value)} className={inputCls} placeholder="2500" data-testid="input-super-area" />
              </div>
              <div>
                <label className={labelCls}>Carpet Area (sq ft)</label>
                <input type="number" value={formData.carpetArea} onChange={e => update('carpetArea', e.target.value)} className={inputCls} placeholder="1800" data-testid="input-carpet-area" />
              </div>
            </div>
          </div>
        )}

        {/* Section 2: LLP Details */}
        {activeSection === 'llp' && (
          <div className="border border-white/10 p-8 mb-8" data-testid="section-llp">
            <h2 className="text-lg text-white mb-6">LLP Details</h2>
            <p className="text-sm text-gray-500 mb-6">Each unit has its own LLP entity. These details populate the LLP agreement.</p>
            <div className="grid grid-cols-2 gap-6">
              <div className="col-span-2">
                <label className={labelCls}>LLP Name *</label>
                <input required value={formData.llpName} onChange={e => update('llpName', e.target.value)} className={inputCls} placeholder="e.g., Per Annum Unit 3201 LLP" data-testid="input-llp-name" />
              </div>
              <div>
                <label className={labelCls}>LLPIN (Registration Number)</label>
                <input value={formData.llpin} onChange={e => update('llpin', e.target.value)} className={inputCls} placeholder="ABZ-0405" data-testid="input-llpin" />
                <p className="text-xs text-gray-600 mt-1">LLP Identification Number from MCA</p>
              </div>
              <div>
                <label className={labelCls}>LLP Effective Date</label>
                <input type="date" value={formData.llpEffectiveDate} onChange={e => update('llpEffectiveDate', e.target.value)} className={inputCls} data-testid="input-llp-effective-date" />
                <p className="text-xs text-gray-600 mt-1">Date from which the LLP agreement takes effect</p>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>LLP Registered Office</label>
                <select value={formData.llpRegisteredOfficeType} onChange={e => handleOfficeChange(e.target.value)} className={`${inputCls} mb-2`} data-testid="select-llp-office">
                  {DEFAULT_ADDRESSES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                  <option value="custom">+ Add New Address</option>
                </select>
                {formData.llpRegisteredOfficeType === 'custom' && (
                  <input value={formData.llpRegisteredOffice} onChange={e => update('llpRegisteredOffice', e.target.value)} className={inputCls} placeholder="Enter full registered office address" data-testid="input-llp-office-custom" />
                )}
              </div>
              <div className="col-span-2 border-t border-white/10 pt-6 mt-2">
                <h3 className="text-sm text-gray-400 mb-4">LLP Bank Account</h3>
              </div>
              <div>
                <label className={labelCls}>Bank Account Name</label>
                <input value={formData.llpBankAccountName} onChange={e => update('llpBankAccountName', e.target.value)} className={inputCls} placeholder="e.g., Per Annum Unit 3201 LLP" data-testid="input-llp-bank-acct-name" />
              </div>
              <div>
                <label className={labelCls}>Bank Name</label>
                <input value={formData.llpBankName} onChange={e => update('llpBankName', e.target.value)} className={inputCls} placeholder="HDFC Bank" data-testid="input-llp-bank" />
              </div>
              <div>
                <label className={labelCls}>IFSC Code</label>
                <input value={formData.llpIfsc} onChange={e => update('llpIfsc', e.target.value)} className={`${inputCls} font-mono`} placeholder="HDFC0001234" data-testid="input-llp-ifsc" />
              </div>
              <div>
                <label className={labelCls}>Account Number</label>
                <input value={formData.llpAccountNumber} onChange={e => update('llpAccountNumber', e.target.value)} className={`${inputCls} font-mono`} placeholder="50200012345678" data-testid="input-llp-account" />
              </div>
            </div>
          </div>
        )}

        {/* Section 3: Designated Partners */}
        {activeSection === 'designated-partners' && (
          <div className="border border-white/10 p-8 mb-8" data-testid="section-designated-partners">
            <h2 className="text-lg text-white mb-2">Designated Partners</h2>
            <p className="text-sm text-gray-500 mb-6">Two designated partners are required per LLP for governance and regulatory compliance.</p>
            <div className="space-y-6">
              <div className="border border-white/10 p-6 bg-white/[0.02]">
                <h3 className="text-sm text-[#4ADE80] uppercase tracking-wider mb-4">Designated Partner 1</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>Full Name *</label>
                    <input value={formData.dp1Name} onChange={e => update('dp1Name', e.target.value)} className={inputCls} placeholder="e.g., Vishal Agarwal" data-testid="input-dp1-name" />
                  </div>
                  <div>
                    <label className={labelCls}>DIN / DPIN</label>
                    <input value={formData.dp1Din} onChange={e => update('dp1Din', e.target.value)} className={`${inputCls} font-mono`} placeholder="e.g., 10776775" data-testid="input-dp1-din" />
                    <p className="text-xs text-gray-600 mt-1">Director Identification Number from MCA</p>
                  </div>
                </div>
              </div>
              <div className="border border-white/10 p-6 bg-white/[0.02]">
                <h3 className="text-sm text-[#4ADE80] uppercase tracking-wider mb-4">Designated Partner 2</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>Full Name *</label>
                    <input value={formData.dp2Name} onChange={e => update('dp2Name', e.target.value)} className={inputCls} placeholder="e.g., Mukund Mittal" data-testid="input-dp2-name" />
                  </div>
                  <div>
                    <label className={labelCls}>DIN / DPIN</label>
                    <input value={formData.dp2Din} onChange={e => update('dp2Din', e.target.value)} className={`${inputCls} font-mono`} placeholder="e.g., 10876543" data-testid="input-dp2-din" />
                    <p className="text-xs text-gray-600 mt-1">Director Identification Number from MCA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section 4: Financial */}
        {activeSection === 'financial' && (
          <div className="border border-white/10 p-8 mb-8" data-testid="section-financial">
            <h2 className="text-lg text-white mb-6">Financial Details</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Unit Purchase Price (INR) *</label>
                <input required type="number" value={formData.unitValue} onChange={e => update('unitValue', e.target.value)} className={`${inputCls} font-mono`} data-testid="input-unit-value" />
                <p className="text-xs text-gray-600 mt-1">Total cost as per Builder-Buyer Agreement</p>
              </div>
              <div>
                <label className={labelCls}>Capital Target (INR) *</label>
                <div className="flex gap-2">
                  <input required type="number" value={formData.capitalTarget} onChange={e => update('capitalTarget', e.target.value)} className={`flex-1 ${inputCls} font-mono`} data-testid="input-capital-target" />
                  <button type="button" onClick={autoCalculateTarget} className="px-4 border border-white/10 text-gray-500 hover:text-white text-sm" title="Set to 30% of unit value">30%</button>
                </div>
                <p className="text-xs text-gray-600 mt-1">Amount to raise from investors for this unit</p>
              </div>
              <div>
                <label className={labelCls}>Min Capital Contribution (INR) *</label>
                <input type="number" value={formData.minInvestment} onChange={e => update('minInvestment', e.target.value)} className={`${inputCls} font-mono`} data-testid="input-min-investment" />
                <p className="text-xs text-gray-600 mt-1">Minimum per investor for this unit. Overrides platform default.</p>
              </div>
              <div>
                <label className={labelCls}>Max Investment (INR)</label>
                <input type="number" value={formData.maxInvestment} onChange={e => update('maxInvestment', e.target.value)} className={`${inputCls} font-mono`} data-testid="input-max-investment" />
              </div>
            </div>
          </div>
        )}

        {/* Section 5: Documents */}
        {activeSection === 'documents' && (
          <div className="border border-white/10 p-8 mb-8" data-testid="section-documents">
            <h2 className="text-lg text-white mb-2">Unit Documents</h2>
            <p className="text-sm text-gray-500 mb-6">Upload key legal documents for this unit. PDF format only, max 10MB each.</p>
            <div className="space-y-6">
              {/* Allotment Letter */}
              <div className="border border-white/10 p-6 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm text-white">Allotment Letter</h3>
                    <p className="text-xs text-gray-500 mt-1">Builder's allotment letter for this unit</p>
                  </div>
                  {documents.allotmentLetter && (
                    <button type="button" onClick={() => removeFile('allotmentLetter')} className="p-1.5 text-gray-500 hover:text-red-400" data-testid="remove-allotment">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {documents.allotmentLetter ? (
                  <div className="flex items-center gap-3 p-3 bg-[#4ADE80]/10 border border-[#4ADE80]/20">
                    <FileText className="w-5 h-5 text-[#4ADE80]" />
                    <div className="flex-1">
                      <p className="text-sm text-white">{documents.allotmentLetter.name}</p>
                      <p className="text-xs text-gray-500">{(documents.allotmentLetter.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => allotmentRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 p-4 border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
                    data-testid="upload-allotment">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Upload Allotment Letter (PDF)</span>
                  </button>
                )}
                <input ref={allotmentRef} type="file" accept=".pdf" className="hidden" onChange={e => handleFileUpload('allotmentLetter', e.target.files[0])} />
              </div>

              {/* Builder-Buyer Agreement */}
              <div className="border border-white/10 p-6 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm text-white">Builder-Buyer Agreement (BBA)</h3>
                    <p className="text-xs text-gray-500 mt-1">Executed BBA between builder and the LLP</p>
                  </div>
                  {documents.bba && (
                    <button type="button" onClick={() => removeFile('bba')} className="p-1.5 text-gray-500 hover:text-red-400" data-testid="remove-bba">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {documents.bba ? (
                  <div className="flex items-center gap-3 p-3 bg-[#4ADE80]/10 border border-[#4ADE80]/20">
                    <FileText className="w-5 h-5 text-[#4ADE80]" />
                    <div className="flex-1">
                      <p className="text-sm text-white">{documents.bba.name}</p>
                      <p className="text-xs text-gray-500">{(documents.bba.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => bbaRef.current?.click()}
                    className="w-full flex items-center justify-center gap-2 p-4 border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
                    data-testid="upload-bba">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Upload BBA (PDF)</span>
                  </button>
                )}
                <input ref={bbaRef} type="file" accept=".pdf" className="hidden" onChange={e => handleFileUpload('bba', e.target.files[0])} />
              </div>

              {/* BBA Date */}
              <div className="max-w-sm">
                <label className={labelCls}>BBA Execution Date</label>
                <input type="date" value={formData.bbaDate} onChange={e => update('bbaDate', e.target.value)} className={inputCls} data-testid="input-bba-date" />
                <p className="text-xs text-gray-600 mt-1">Date on which the BBA was signed — used for appreciation calculations</p>
              </div>
            </div>
          </div>
        )}

        {/* Section 6: Agreement Terms */}
        {activeSection === 'agreement' && (
          <div className="border border-white/10 p-8 mb-8" data-testid="section-agreement">
            <h2 className="text-lg text-white mb-2">Agreement Terms</h2>
            <p className="text-sm text-gray-500 mb-6">These fields populate the LLP Agreement sent via Zoho Sign</p>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Minimum Holding Period *</label>
                <select value={formData.holdingPeriod} onChange={e => update('holdingPeriod', e.target.value)} className={inputCls} data-testid="select-holding-period">
                  <option value="3 Months">3 Months</option>
                  <option value="6 Months">6 Months</option>
                  <option value="12 Months">12 Months</option>
                  <option value="18 Months">18 Months</option>
                  <option value="24 Months">24 Months</option>
                </select>
                <p className="text-xs text-gray-600 mt-1">Partners can exit only after this period + 6 month lock-in</p>
              </div>
              <div>
                <label className={labelCls}>Target Appreciation *</label>
                <input value={formData.targetAppreciation} onChange={e => update('targetAppreciation', e.target.value)} className={inputCls} placeholder="5% annualised increase in Book Value" data-testid="input-target-appreciation" />
                <p className="text-xs text-gray-600 mt-1">Benchmark from BBA signing date</p>
              </div>
            </div>
          </div>
        )}

        {/* Section 7: Payment Schedule (Zoho Annexure-D) */}
        {activeSection === 'payment-plan' && (
          <div className="border border-white/10 p-8 mb-8" data-testid="section-payment-plan">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg text-white">Payment Schedule (Annexure-D)</h2>
                <p className="text-sm text-gray-500 mt-1">Builder milestone payment schedule. Populates LLP agreement Annexure-D. Capital calls to investors will be derived from this schedule.</p>
              </div>
              <div className="flex items-center gap-3">
                <button type="button" onClick={applyPaymentPlanTemplate} className="px-3 py-1.5 border border-white/10 text-xs text-gray-400 hover:text-white">
                  Apply Template
                </button>
                <span className={`px-3 py-1 text-sm font-mono ${ppTotal === 100 ? 'bg-[#4ADE80]/20 text-[#4ADE80]' : 'bg-red-500/20 text-red-400'}`}>
                  {ppTotal}% / 100%
                </span>
              </div>
            </div>

            {firstSlotNotBooking && (
              <div className="flex items-center gap-2 mb-4 p-3 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                First milestone is typically "On Booking" — verify this is intentional
              </div>
            )}

            <div className="space-y-3">
              {paymentPlan.map((slot, idx) => (
                <div key={idx} className="border border-white/10 p-4 bg-white/5">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/10 text-white text-sm font-mono flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Percentage (%)</label>
                        <input type="number" min="1" max="100" step="1" value={slot.percentage}
                          onChange={e => updatePaymentSlot(idx, 'percentage', e.target.value)}
                          className="w-full bg-black border border-white/10 px-3 py-2 text-white text-sm font-mono"
                          data-testid={`pp-percentage-${idx}`} />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Milestone *</label>
                        <input value={slot.milestone}
                          onChange={e => updatePaymentSlot(idx, 'milestone', e.target.value)}
                          className="w-full bg-black border border-white/10 px-3 py-2 text-white text-sm"
                          placeholder="e.g., On Booking"
                          data-testid={`pp-milestone-${idx}`} />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Expected Date</label>
                        <input type="month" value={slot.expectedDate}
                          onChange={e => updatePaymentSlot(idx, 'expectedDate', e.target.value)}
                          className="w-full bg-black border border-white/10 px-3 py-2 text-white text-sm"
                          data-testid={`pp-expected-date-${idx}`} />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Description</label>
                        <input value={slot.description}
                          onChange={e => updatePaymentSlot(idx, 'description', e.target.value)}
                          className="w-full bg-black border border-white/10 px-3 py-2 text-white text-sm"
                          placeholder="Optional details"
                          data-testid={`pp-description-${idx}`} />
                      </div>
                    </div>
                    <button type="button" onClick={() => removePaymentSlot(idx)} className="p-2 text-gray-500 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {paymentPlan.length < 5 && (
              <button type="button" onClick={addPaymentSlot}
                className="mt-4 flex items-center gap-2 px-4 py-2 border border-dashed border-white/20 text-gray-400 hover:text-white w-full justify-center"
                data-testid="add-payment-slot">
                <Plus className="w-4 h-4" /> Add Milestone ({paymentPlan.length}/5)
              </button>
            )}
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center gap-4 mt-8">
          <button type="button" onClick={onBack} className="border border-white/10 px-8 py-3 text-white">Cancel</button>
          <button type="submit" disabled={loading || ppTotal !== 100}
            className={`font-semibold px-8 py-3 ${ppTotal === 100 ? 'bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}
            data-testid="submit-unit">
            {loading ? 'Creating Unit...' : 'Create Unit'}
          </button>
          {ppTotal !== 100 && (
            <span className="text-sm text-red-400">
              Payment Schedule: {ppTotal}%/100%
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
